import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AccountingReferenceType } from "../../context/contextTypes";

import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import Sheet from "components/sheet/Sheet";
import { Select as SelectNew, Select } from "components/select/";
import { ITournament } from "redux/reducers/models/tournament";
import Icon from "components/icon/Icon";
import { faTimes } from "@fortawesome/pro-regular-svg-icons";

import { StatusCode } from "api/protocols";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";
import { GetTournament } from "api/rpc/facilityAdmin/tournament/tournament";
import Checkbox from "components/form/checkbox/Checkbox";
import { useCustomerContext } from "../../context/CustomerContext";
import { capitalize } from "helpers/Helpers";

export type EditHouseAccountState = {
  title: string;
  limit: string;
  account_number: string;
  accounting_reference_id: number;
  tournament_id: number;
  global: boolean;
  sendStatement: boolean;
  auto_pay_balance: boolean;
  auto_pay_anchor_date: number;
  send_statement: boolean;
  default_payment_method_id: number,
};

type EditCustomerHouseAccountModalProps = {
  accountingReferenceTypes: AccountingReferenceType[] | null;
  isVisible: boolean;
  onClose: () => void;
  editAccount: Partial<EditHouseAccountState>;
  setEditAccount: React.Dispatch<React.SetStateAction<Partial<EditHouseAccountState>>>;
  onOk: (editedHouseAccount: any) => void;
};

interface IFilterState {
  tournaments: Array<ITournament>;
  tournamentQuery: string;
  tournamentSearching: boolean;
  selectedTournament: ITournament;
}

export function EditCustomerHouseAccountModal(props: EditCustomerHouseAccountModalProps) {
  const { t } = useTranslation();
  const { Option } = SelectNew;
  const dispatch = useAppDispatch();
  const { state } = useCustomerContext();

  const minimumDayInput = 1;
  const maximumDayInput = 31;

  const [tournaments, setTournaments] = useState<Array<ITournament>>([]);

  const { isVisible, accountingReferenceTypes, editAccount, setEditAccount, onClose, onOk } = props;

  useEffect(() => {
    void loadTournaments();
  }, []);

  useEffect(() => {
    assignDefaultTournament();
  }, [props.isVisible]);

  function handleChange(event: any) {
    const id = event?.target?.id;
    const value = event?.target?.value;

    if (id === undefined || value === undefined) {
      return;
    }

    setEditAccount({ ...editAccount, [id]: value });
  }

  const [filterState, setFilterState] = useState<IFilterState>({
    tournamentQuery: "",
    tournamentSearching: false,
    selectedTournament: null,
    tournaments: [],
  });

  function handleTournamentChange(value: any, id: string, tournament: ITournament) {
    if (tournament) {
      setEditAccount(prevState => ({ ...prevState, [id]: tournament.id === -1 ? null : tournament?.id }));
      setFilterState(prevState => ({ ...prevState, tournamentQuery: "", selectedTournament: tournament }));
    }
  }

  function removeSelectedTournament() {
    setFilterState(prevState => ({
      ...prevState,
      selectedTournament: null,
      tournaments: [],
      tournamentSearching: false,
    }));
    setEditAccount(prevState => ({ ...prevState, tournament_id: null }));
  }

  useEffect(() => {
    let mounted = true;
    let timeoutId: NodeJS.Timeout = null;
    const search = () => {
      timeoutId = global.setTimeout(() => {
        void (async () => {
          if (filterState.tournamentQuery !== "") {
            try {
              setFilterState(prevState => ({ ...prevState, tournamentSearching: true }));
              const tournaments = await loadSearchedTournaments(filterState.tournamentQuery);
              if (mounted) {
                setFilterState(prevState => ({ ...prevState, tournaments: tournaments, tournamentSearching: false }));
              }
            } catch (error) {
              console.log("err", error);
            }
            return;
          } else {
            setFilterState(prevState => ({ ...prevState, tournaments: [], tournamentSearching: false }));
          }
        })();
      }, 1000);
    };
    search();
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [filterState.tournamentQuery]);

  async function loadSearchedTournaments(search: string) {
    const params = {
      search: search,
    };

    const tournamentRes = await GetTournament(params, false);

    if (tournamentRes.status !== StatusCode.OK) {
      dispatch(showError(tournamentRes.message));
      return [];
    }

    return tournamentRes.data;
  }

  function displayTournaments() {
    if (filterState?.tournaments && filterState?.tournaments?.length > 0 && filterState?.tournamentQuery !== "") {
      return filterState?.tournaments;
    } else {
      return tournaments;
    }
  }

  async function loadTournaments() {
    try {
      const tournamentRes = await GetTournament(null, false);

      if (tournamentRes.status === StatusCode.OK) {
        setTournaments(tournamentRes.data);
      } else {
        dispatch(showError(tournamentRes.message));
      }
    } catch (e) {
      dispatch(showError(String(e)));
    }
  }

  function assignDefaultTournament() {
    let currentTournament: ITournament;

    for (let i = 0; i < tournaments?.length; i++) {
      if (tournaments[i].id === editAccount.tournament_id) {
        currentTournament = tournaments[i];
      }
    }

    setFilterState(prevState => ({ ...prevState, selectedTournament: currentTournament }));
  }

  return (
    <Sheet
      open={isVisible}
      size="medium"
      height="flexible"
      closable
      title={t("Edit Account")}
      onCancel={() => onClose()}
      onOk={() => onOk(editAccount)}
      cancelText={t("Cancel")}
      okText={t("Update Account")}
      overflow
    >
      <Form>
        <FormLayout>
          <FormLayout.Group>
            <Input
              value={editAccount.title}
              label={t("Title")}
              id="title"
              onChange={handleChange}
            />

            <Input
              value={editAccount.account_number}
              id="account_number"
              label={t("Account Number")}
              onChange={handleChange}
            />
          </FormLayout.Group>
          <FormLayout.Group>
            <Input
              value={editAccount.limit}
              label={t("Limit")}
              id="limit"
              onChange={handleChange}
            />
            {accountingReferenceTypes && (
              <SelectNew
                label={t("Accounting Reference")}
                defaultValue={editAccount.accounting_reference_id}
                onChange={(value: number) => setEditAccount({ ...editAccount, accounting_reference_id: value })}
              >
                {accountingReferenceTypes.map((type, index: number) => {
                  return (
                    <Option key={index} value={type.value} name={type.label}>
                      <span>{type.label}</span>
                    </Option>
                  );
                })}
              </SelectNew>
            )}
          </FormLayout.Group>



          <FormLayout.Group>
            {filterState.selectedTournament ? (
              <div>
                <div>
                  <p className="event-label">
                    {t("Tournament")}
                  </p>
                </div>

                <div className="selected-container">
                  <div className="event-name">
                    <div>{filterState.selectedTournament.name}</div>

                    <div>
                      <button className="" onClick={removeSelectedTournament}>
                        <Icon icon={faTimes} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <Select
                  showSearch
                  label={t("Tournament")}
                  onSearch={(query: string) =>
                    setFilterState(prevState => ({ ...prevState, tournamentQuery: query }))
                  }
                  onChange={(value: any, tournament: ITournament) =>
                    handleTournamentChange(value, "tournament_id", tournament)
                  }
                  placeholder={t("Search tournaments...")}
                  allowClear
                  searchValue={filterState.tournamentQuery}
                  showDropDownOnFocus={true}
                  searching={filterState.tournamentSearching}
                >
                  <Option value={-1} extraValues={{ name: "None", id: -1 }}>
                    <div className="text-semibold text-lg">
                      {t("None")}
                    </div>
                  </Option>
                  {displayTournaments()?.map((tournament: any, index: number) => {
                    return (
                      <Option value={tournament?.id} extraValues={tournament} key={index}>
                        <div className="text-semibold text-lg">{tournament.name}</div>
                      </Option>
                    );
                  })}
                </Select>
              </div>
            )}
          </FormLayout.Group>
          <FormLayout.Group>
            <Checkbox
              id="global"
              size="medium"
              value={editAccount.global}
              checked={editAccount.global}
              onChange={({ target }) =>
                setEditAccount({ ...editAccount, [target.id]: target.value === "true" ? false : true })
              }
              label={t("This account can be used across all courses")}
            />

            {<Checkbox
              id="sendStatement"
              size="medium"
              value={editAccount.sendStatement}
              checked={editAccount.sendStatement}
              onChange={({ target }) =>
                setEditAccount({ ...editAccount, [target.id]: target.value === "true" ? false : true })
              }
              label={t("Send Monthly Account Statements")}
              helpText={t("On the 5th of every month, the customer will receive the previous months statement via email")}
            />}

            <Checkbox
              id="auto_pay_balance"
              size="medium"
              value={editAccount.auto_pay_balance}
              checked={editAccount.auto_pay_balance}
              onChange={({ target }) =>
                setEditAccount({ ...editAccount, [target.id]: target.value === "true" ? false : true })
              }
              label={"Auto Pay Balance"}
            />
          </FormLayout.Group>

          {editAccount?.auto_pay_balance ?
            <FormLayout.Group>
              <Input
                label={t("Auto Pay Date")}
                type="number"
                id="auto_pay_anchor_date"
                value={
                  editAccount?.auto_pay_anchor_date < minimumDayInput ? "" :
                    editAccount?.auto_pay_anchor_date > maximumDayInput ? maximumDayInput : editAccount?.auto_pay_anchor_date}
                onChange={e => setEditAccount(prev => ({ ...prev, auto_pay_anchor_date: Number(e.target?.value) }))}
                prefix={t("Day of Month")}
                error={editAccount?.auto_pay_anchor_date === 0}
              />

              {state?.paymentMethods?.length > 0 ?
                <SelectNew
                  label={t("Payment Methods")}
                  defaultValue={editAccount?.default_payment_method_id}
                  onChange={(value: number) => setEditAccount({ ...editAccount, default_payment_method_id: value })}
                >
                  {state?.paymentMethods?.map((method, index: number) => {
                    return (
                      <Option key={index} value={method.id} name={method.type}>
                        {capitalize(method?.brand)} •••• •••• •••• {method?.last4}
                      </Option>
                    );
                  })}
                </SelectNew>
                : null}
            </FormLayout.Group>
            : null}

        </FormLayout>
      </Form>
    </Sheet>
  );
}
